import { useTranslation } from 'react-i18next';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { useEvent, useUpdateEvent } from '@/api/hooks/useEvents';

const updateEventSchema = z.object({
  title: z.string().min(2, 'events.validation.titleRequired').max(300),
  event_date: z.string().min(1, 'events.validation.dateRequired'),
  description: z.string().max(5000).optional(),
  venue_name: z.string().max(300).optional(),
  partner_1_name: z.string().max(200).optional(),
  partner_2_name: z.string().max(200).optional(),
});

type UpdateEventForm = z.infer<typeof updateEventSchema>;

export function EventEditPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: event, isLoading, isError } = useEvent(id || '');
  const updateEvent = useUpdateEvent(id || '');

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<UpdateEventForm>({
    resolver: zodResolver(updateEventSchema),
    values: event
      ? {
          title: event.title,
          event_date: event.event_date,
          description: event.description || '',
          venue_name: event.venue_name || '',
          partner_1_name: event.partner_1_name || '',
          partner_2_name: event.partner_2_name || '',
        }
      : undefined,
  });

  const onSubmit = (data: UpdateEventForm) => {
    updateEvent.mutate(
      {
        title: data.title,
        event_date: data.event_date,
        description: data.description || undefined,
        venue_name: data.venue_name || undefined,
        partner_1_name: data.partner_1_name || undefined,
        partner_2_name: data.partner_2_name || undefined,
      },
      {
        onSuccess: () => {
          navigate(`/events/${id}`);
        },
      },
    );
  };

  if (isLoading) {
    return (
      <div className="page-container">
        <div className="flex justify-center py-20">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
        </div>
      </div>
    );
  }

  if (isError || !event) {
    return (
      <div className="page-container">
        <div className="card text-center">
          <p className="text-sm text-red-600 dark:text-red-400">{t('events.loadError')}</p>
          <Link to="/events" className="btn-secondary mt-4">
            {t('actions.back')}
          </Link>
        </div>
      </div>
    );
  }

  const isDisabled = isSubmitting || updateEvent.isPending;

  return (
    <div className="page-container">
      <div className="mb-6 flex items-center gap-3">
        <Link
          to={`/events/${id}`}
          className="rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-800"
        >
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <h1 className="page-title">
          {t('actions.edit')} - {event.title}
        </h1>
      </div>

      <div className="card max-w-2xl">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          {/* Title */}
          <div>
            <label
              htmlFor="title"
              className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              {t('events.fields.title')} <span className="text-red-500">*</span>
            </label>
            <input
              id="title"
              type="text"
              className={`input-field ${errors.title ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
              placeholder={t('events.fields.titlePlaceholder')}
              {...register('title')}
            />
            {errors.title && (
              <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                {t(errors.title.message || 'events.validation.titleRequired')}
              </p>
            )}
          </div>

          {/* Event Date */}
          <div>
            <label
              htmlFor="event_date"
              className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              {t('events.fields.eventDate')} <span className="text-red-500">*</span>
            </label>
            <input
              id="event_date"
              type="date"
              className={`input-field ${errors.event_date ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
              {...register('event_date')}
            />
            {errors.event_date && (
              <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                {t(errors.event_date.message || 'events.validation.dateRequired')}
              </p>
            )}
          </div>

          {/* Venue */}
          <div>
            <label
              htmlFor="venue_name"
              className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              {t('events.fields.venueName')}
            </label>
            <input
              id="venue_name"
              type="text"
              className="input-field"
              placeholder={t('events.fields.venueNamePlaceholder')}
              {...register('venue_name')}
            />
          </div>

          {/* Partner Names */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label
                htmlFor="partner_1_name"
                className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                {t('events.fields.partner1Name')}
              </label>
              <input
                id="partner_1_name"
                type="text"
                className="input-field"
                placeholder={t('events.fields.partner1Placeholder')}
                {...register('partner_1_name')}
              />
            </div>
            <div>
              <label
                htmlFor="partner_2_name"
                className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                {t('events.fields.partner2Name')}
              </label>
              <input
                id="partner_2_name"
                type="text"
                className="input-field"
                placeholder={t('events.fields.partner2Placeholder')}
                {...register('partner_2_name')}
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label
              htmlFor="description"
              className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              {t('events.fields.description')}
            </label>
            <textarea
              id="description"
              rows={3}
              className="input-field resize-none"
              placeholder={t('events.fields.descriptionPlaceholder')}
              {...register('description')}
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button type="submit" className="btn-primary" disabled={isDisabled}>
              {isDisabled ? t('status.saving') : t('actions.save')}
            </button>
            <Link to={`/events/${id}`} className="btn-secondary">
              {t('actions.cancel')}
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
